import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MoreHorizontal, Package, Paperclip, Plus, Send, Store, Upload } from 'lucide-react';
import { EntityType, isPositiveAmount, type EventBooking } from '@peles/shared';
import { EntityList, StatusBadge, type EntityListColumn } from '../common/EntityList';
import { SearchableSelect } from '../common/SearchableSelect';
import { DropdownMenu } from '../common/DropdownMenu';
import { SendVendorListModal } from '../vendors/SendVendorListModal';
import { ComposeVendorEmailModal } from '../vendors/ComposeVendorEmailModal';
import { useDialog } from '../../context/DialogContext';
import { useCan } from '../../hooks/useCan';
import { useEventBookings } from '../../hooks/useBookings';
import { useCreateEntityLink, useDeleteEntityLink } from '../../hooks/useEntityLinks';
import { useEventVendorRecipients } from '../../hooks/useEvents';
import { useThreadCounts } from '../../hooks/useThreadCounts';
import { RecordConversations } from '../messaging/RecordConversations';
import { VendorReviewModal } from '../vendors/VendorReviewModal';
import { extractApiErrorMessage } from '../../utils/apiError';
import { BookingSheet, isBookingSheetTab, type BookingSheetTab } from './BookingSheet';
import {
  coiWarning,
  contractWarning,
  describeBooking,
  describeBookingMoney,
  paidPercent,
} from './bookingDisplay';
import {
  BookingCard,
  BookingMoneyLine,
  BookingStageBadge,
  type BookingCardMoney,
} from './BookingCard';

export interface BookingListProps {
  eventId: string;
  eventName?: string;
  eventDate?: string;
  venueId?: string | null;
  currentUser?: { email: string; name: string };
}

/**
 * The Vendors view: the event's vendors after the decision — one row per
 * vendor, with its stage, what is booked with it and where the money stands.
 * A vendor opens its booking sheet (`?booking=<linkId>`). Candidates asked for
 * a quote are not here: a vendor joins on accept, on "Book directly", or when
 * the office adds one by hand (the venue's own caterer).
 */
export function BookingList({
  eventId,
  eventName,
  eventDate,
  venueId,
  currentUser,
}: BookingListProps) {
  const { data, isLoading, isError, refetch } = useEventBookings(eventId);
  const { data: recipients = [] } = useEventVendorRecipients(eventId);
  const [params, setParams] = useSearchParams();
  const { confirm, alert } = useDialog();
  const canEmailVendors = useCan('messages.vendors');
  const createLink = useCreateEntityLink();
  const deleteLink = useDeleteEntityLink();

  const [adding, setAdding] = useState(false);
  const [newVendorId, setNewVendorId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [composing, setComposing] = useState(false);
  const [sendingList, setSendingList] = useState(false);
  /** The vendor whose name was pressed: its contacts and how to pay it. */
  const [reviewing, setReviewing] = useState<EventBooking | null>(null);

  const bookings = useMemo(() => data?.bookings ?? [], [data]);
  const stages = useMemo(() => data?.stages ?? [], [data]);

  /*
   * Every row's conversation badge in one request (COMMUNICATIONS-DESIGN H3),
   * keyed by the **vendor** — `Vendor` is the record, as on the Vendors page,
   * never the event–vendor link. The icon goes in this list's own actions,
   * which the vendor portal's read-only `BookingCard` never carries.
   */
  const { countFor: threadCountFor, refetch: refetchThreadCounts } = useThreadCounts(
    'Vendor',
    bookings.map((b) => b.vendorId),
  );
  const onThreadActivity = useCallback(() => {
    void refetchThreadCounts();
  }, [refetchThreadCounts]);

  const recipientByVendor = useMemo(
    () => new Map(recipients.map((r) => [r.vendorId, r])),
    [recipients],
  );
  const selectedRecipients = useMemo(
    () =>
      [...selected]
        .map((vendorId) => recipientByVendor.get(vendorId))
        .filter((r): r is NonNullable<typeof r> => !!r?.email),
    [selected, recipientByVendor],
  );
  const emailable = (b: EventBooking) => !!recipientByVendor.get(b.vendorId)?.email;

  const openId = params.get('booking');
  const openBooking = bookings.find((b) => b.linkId === openId) ?? null;
  const tabParam = params.get('tab');
  const tab: BookingSheetTab = isBookingSheetTab(tabParam) ? tabParam : 'details';

  const openSheet = useCallback(
    (booking: EventBooking | null, next: BookingSheetTab = 'details') =>
      setParams(
        (current) => {
          const p = new URLSearchParams(current);
          if (booking) {
            p.set('booking', booking.linkId);
            p.set('tab', next);
          } else {
            p.delete('booking');
            p.delete('tab');
          }
          return p;
        },
        { replace: !!booking && booking.linkId === openId },
      ),
    [setParams, openId],
  );

  const openNeed = (needId: string) =>
    setParams((current) => {
      const p = new URLSearchParams(current);
      p.delete('booking');
      p.set('view', 'plan');
      p.set('need', needId);
      p.set('tab', 'details');
      return p;
    });

  const toggle = (vendorId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });

  const add = () => {
    if (!newVendorId) return;
    createLink.mutate(
      {
        sourceEntityType: EntityType.EVENT,
        sourceEntityId: eventId,
        targetEntityType: EntityType.VENDOR,
        targetEntityId: newVendorId,
        linkType: 'VENDOR',
      },
      {
        onSuccess: () => {
          setNewVendorId('');
          setAdding(false);
        },
        onError: (err) =>
          void alert({
            title: 'Vendor not added',
            message: extractApiErrorMessage(err, 'The vendor could not be added to this event.'),
            variant: 'error',
          }),
      },
    );
  };

  const remove = async (b: EventBooking) => {
    const ok = await confirm({
      title: 'Remove vendor',
      message: `Remove ${b.vendorName} from this event?`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!ok) return;
    deleteLink.mutate(b.linkId, {
      onError: (err) =>
        void alert({
          title: 'Not removed',
          message: extractApiErrorMessage(err, 'The vendor could not be removed.'),
          variant: 'error',
        }),
    });
  };

  const menu = (b: EventBooking) => (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu
        align="end"
        ariaLabel={`Actions for ${b.vendorName}`}
        items={[
          { id: 'details', label: 'Details', onSelect: () => openSheet(b, 'details') },
          { id: 'needs', label: 'Needs', onSelect: () => openSheet(b, 'needs') },
          { id: 'contacts', label: 'Contacts', onSelect: () => openSheet(b, 'contacts') },
          { id: 'files', label: 'Files', onSelect: () => openSheet(b, 'files') },
          // A vendor with work booked stays: the server refuses it too, since
          // the row would vanish from this view with the work still booked.
          ...(b.needs.length === 0
            ? [
                {
                  id: 'remove',
                  label: <span className="text-red-600">Remove from event</span>,
                  dividerBefore: true,
                  onSelect: () => void remove(b),
                },
              ]
            : []),
        ]}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={`Actions for ${b.vendorName}`}
            className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <MoreHorizontal size={16} />
          </button>
        )}
      />
    </div>
  );

  /**
   * The vendor's files at a glance: a paperclip with how many there are, else
   * an upload mark — either opens the booking's Files tab. The count comes with
   * the bookings, so no row fetches its own.
   */
  const filesButton = (b: EventBooking) => {
    const count = b.attachmentCount;
    const label =
      count > 0
        ? `${count} file${count === 1 ? '' : 's'} for ${b.vendorName}`
        : `Add a file for ${b.vendorName}`;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openSheet(b, 'files');
        }}
        title={label}
        aria-label={label}
        className="relative min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100"
      >
        {count > 0 ? (
          <>
            <Paperclip size={16} className="text-primary-600" />
            <span className="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold bg-primary-600 text-white rounded-full flex items-center justify-center">
              {count}
            </span>
          </>
        ) : (
          <Upload size={16} />
        )}
      </button>
    );
  };

  const rowActions = (b: EventBooking) => (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      <RecordConversations
        entityType="Vendor"
        entityId={b.vendorId}
        variant="icon"
        count={threadCountFor(b.vendorId)}
        onActivity={onThreadActivity}
      />
      {filesButton(b)}
      {menu(b)}
    </div>
  );

  const checkbox = (b: EventBooking) =>
    canEmailVendors ? (
      <input
        type="checkbox"
        checked={selected.has(b.vendorId)}
        disabled={!emailable(b)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggle(b.vendorId)}
        aria-label={`Select ${b.vendorName} to email`}
        title={emailable(b) ? undefined : 'No email on the vendor or its contacts'}
        className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
      />
    ) : null;

  /** The card's money, or null with nothing booked. */
  const cardMoney = (b: EventBooking): BookingCardMoney | null => {
    const text = describeBookingMoney(b);
    return text ? { text, paidPercent: paidPercent(b), label: `${b.vendorName} paid` } : null;
  };

  const contractBadge = (b: EventBooking) => {
    const warning = contractWarning(b);
    return warning ? <StatusBadge label={warning.label} color={warning.color} /> : null;
  };

  const moneyLine = (b: EventBooking) => {
    const money = cardMoney(b);
    return money ? (
      <BookingMoneyLine money={money} />
    ) : (
      <span className="text-gray-400">Nothing booked yet</span>
    );
  };

  /*
   * ⚠️ **The name opens the vendor; the rest of the row opens the booking.**
   * The same review the expense list's vendor name opens — contacts, payment
   * methods, the open balance on any pay link — so paying a vendor from here
   * needs no trip to the expenses. Stopped from reaching the row, whose press
   * still opens the booking sheet.
   */
  const vendorNameButton = (b: EventBooking) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setReviewing(b);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      className="font-medium text-gray-900 truncate text-left hover:text-primary-700 hover:underline"
      aria-label={`${b.vendorName}: contacts and how to pay`}
    >
      {b.vendorName}
    </button>
  );

  const columns: EntityListColumn<EventBooking>[] = [
    ...(canEmailVendors ? [{ key: 'select', header: '', width: 'w-10', render: checkbox }] : []),
    {
      key: 'vendor',
      header: 'Vendor',
      render: (b) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {vendorNameButton(b)}
            <BookingStageBadge stage={b.stage} />
            {coiWarning(b) && <StatusBadge label={coiWarning(b)!} color="red" />}
            {contractBadge(b)}
          </div>
          <p className="text-xs text-gray-500 truncate">{describeBooking(b)}</p>
        </div>
      ),
    },
    { key: 'money', header: 'Money', width: 'w-64', render: moneyLine },
    { key: 'actions', header: '', width: 'w-32', align: 'right', render: rowActions },
  ];

  const renderCard = (b: EventBooking) => (
    <BookingCard
      title={vendorNameButton(b)}
      description={describeBooking(b)}
      stage={b.stage}
      warning={coiWarning(b)}
      money={cardMoney(b)}
      noMoneyText="Nothing booked yet"
      leading={checkbox(b)}
      actions={rowActions(b)}
    >
      {contractBadge(b)}
    </BookingCard>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Vendors ({bookings.length})</h3>
        <div className="flex flex-wrap items-center gap-2">
          {canEmailVendors && selectedRecipients.length > 0 && (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1.5 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"
            >
              <Send size={16} />
              Email {selectedRecipients.length} vendor{selectedRecipients.length === 1 ? '' : 's'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
          >
            <Plus size={16} /> Add vendor
          </button>
          <DropdownMenu
            align="end"
            ariaLabel="More vendor actions"
            items={[
              {
                id: 'items',
                label: 'Items',
                icon: <Package size={14} />,
                onSelect: () =>
                  setParams((current) => {
                    const p = new URLSearchParams(current);
                    p.set('view', 'items');
                    p.delete('booking');
                    p.delete('tab');
                    return p;
                  }),
              },
              // Not while the vendors load: the dialog counts and lists them.
              ...(currentUser && eventName && !isLoading
                ? [
                    {
                      id: 'send-list',
                      label: 'Send the vendor list…',
                      icon: <Mail size={14} />,
                      onSelect: () => setSendingList(true),
                    },
                  ]
                : []),
            ]}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <MoreHorizontal size={16} /> More
              </button>
            )}
          />
        </div>
      </div>

      {adding && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <p className="text-xs text-gray-500">
            For a vendor booked outside the Plan — the venue’s own caterer, say. To shop for one,
            add a need on the Plan and ask for quotes.
          </p>
          <SearchableSelect
            entityType="vendor"
            value={newVendorId}
            onChange={(v) => setNewVendorId(typeof v === 'string' ? v : '')}
            placeholder="Search vendors..."
            excludeIds={bookings.map((b) => b.vendorId)}
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="min-h-11 sm:min-h-9 px-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={add}
              disabled={!newVendorId || createLink.isPending}
              className="min-h-11 sm:min-h-9 px-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {isError ? (
        <p className="text-sm text-red-600">
          The vendors could not be loaded.{' '}
          <button
            type="button"
            className="underline min-h-11 sm:min-h-0"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </p>
      ) : (
        <EntityList
          items={bookings}
          columns={columns}
          getKey={(b) => b.linkId}
          isLoading={isLoading}
          onRowClick={(b) => openSheet(b)}
          renderCard={renderCard}
          selectedId={openId}
          emptyIcon={<Store size={40} className="text-gray-300" />}
          emptyMessage="No vendors booked yet. A vendor joins the event when you accept their quote or book a need directly on the Plan."
        />
      )}

      <BookingSheet
        booking={openBooking}
        eventId={eventId}
        stages={stages}
        tab={tab}
        onTabChange={(next) => openBooking && openSheet(openBooking, next)}
        onClose={() => openSheet(null)}
        onOpenNeed={openNeed}
      />

      <VendorReviewModal
        open={reviewing !== null}
        vendorId={reviewing?.vendorId ?? null}
        onClose={() => setReviewing(null)}
        // The Vendors view is the office's (PLANNER+), and the server holds
        // payment-method writes to PLANNER+ either way.
        canManagePayments
        payment={
          reviewing && isPositiveAmount(reviewing.balance)
            ? {
                amount: reviewing.balance,
                label: `Open balance on ${eventName ?? 'this event'}`,
                note: [eventName, reviewing.vendorName].filter(Boolean).join(' — '),
              }
            : undefined
        }
      />

      <ComposeVendorEmailModal
        open={composing}
        onClose={() => setComposing(false)}
        eventId={eventId}
        recipients={selectedRecipients}
      />
      {sendingList && currentUser && eventName && (
        <SendVendorListModal
          eventId={eventId}
          vendors={bookings}
          initialSelectedVendorIds={[...selected]}
          eventName={eventName}
          eventDate={eventDate ?? ''}
          venueId={venueId}
          currentUser={currentUser}
          onClose={() => setSendingList(false)}
        />
      )}
    </div>
  );
}
